import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import api from '../../utils/api';
import {
  Users, Building2, Briefcase, DollarSign, TrendingUp,
  CheckCircle, Clock, AlertTriangle, XCircle, RefreshCw,
  Heart, Star, UserCheck, ChevronRight, Activity, Eye, Globe, Wifi
} from 'lucide-react';

// ── Card de estatística ───────────────────────────────────────
function StatCard({ valor, label, sub, icon: Icon, cor, bg, destaque, to }) {
  const content = (
    <div className={`card p-4 flex items-center gap-3 transition-all ${destaque ? 'ring-2 ring-offset-1 ring-red-300' : 'hover:shadow-md'}`}>
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${bg}`}>
        <Icon size={20} className={cor} />
      </div>
      <div className="flex-1 min-w-0">
        <p className={`font-sora font-black text-2xl leading-none ${cor}`}>
          {valor != null ? Number(valor).toLocaleString('pt-BR') : '—'}
        </p>
        <p className="text-xs text-gray-500 font-medium mt-0.5">{label}</p>
        {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
      </div>
      {to && <ChevronRight size={14} className="text-gray-300 flex-shrink-0" />}
    </div>
  );
  return to ? <Link to={to}>{content}</Link> : content;
}

// ── Barra de progresso simples ────────────────────────────────
function MiniBar({ valor, total, cor }) {
  const pct = total > 0 ? Math.round((valor / total) * 100) : 0;
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${cor}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-gray-400 w-8 text-right">{pct}%</span>
    </div>
  );
}

export default function DashboardAdmin() {
  const [dados,       setDados]       = useState(null);
  const [analytics,   setAnalytics]   = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [atualizadoEm, setAtualizadoEm] = useState(null);

  const carregar = useCallback(async () => {
    try {
      const [dashRes, analyticsRes] = await Promise.allSettled([
        api.get('/admin/dashboard'),
        api.get('/admin/analytics'),
      ]);
      if (dashRes.status === 'fulfilled') setDados(dashRes.value.data);
      if (analyticsRes.status === 'fulfilled') setAnalytics(analyticsRes.value.data);
      setAtualizadoEm(new Date());
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => {
    carregar();
    const iv = setInterval(carregar, 30000); // refresh 30s
    return () => clearInterval(iv);
  }, [carregar]);

  const s  = dados?.stats || {};
  const pr = dados?.pagamentos_recentes || [];
  const ex = dados?.expirando_breve || [];

  const n = (v) => Number(v) || 0;

  return (
    <Layout title="Painel Administrativo" subtitle="Visão geral da plataforma AcreJob">

      {/* Barra de atualização */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${loading ? 'bg-yellow-400 animate-pulse' : 'bg-green-500 animate-pulse'}`}/>
          <span className="text-xs text-gray-400">
            {atualizadoEm
              ? `Atualizado às ${atualizadoEm.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second:'2-digit' })} · auto-refresh 30s`
              : 'Carregando...'}
          </span>
        </div>
        <button onClick={carregar} disabled={loading}
          className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-acre-green border border-gray-200 hover:border-acre-green px-3 py-1.5 rounded-lg transition-colors disabled:opacity-40">
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Atualizar
        </button>
      </div>

      {/* ── Bloco 0: Analytics em tempo real ── */}
      <div className="mb-2">
        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">📊 Tráfego em Tempo Real</p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Link to="/admin/analytics"
          className="card p-4 flex items-center gap-3 hover:shadow-md transition-all">
          <div className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
            <Wifi size={20} className="text-emerald-500"/>
          </div>
          <div>
            <div className="flex items-center gap-1.5">
              <p className="font-sora font-black text-2xl leading-none text-emerald-600">
                {analytics?.online_agora ?? '—'}
              </p>
              <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse flex-shrink-0"/>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">Online agora</p>
          </div>
        </Link>
        <Link to="/admin/analytics"
          className="card p-4 flex items-center gap-3 hover:shadow-md transition-all">
          <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
            <Users size={20} className="text-blue-500"/>
          </div>
          <div>
            <p className="font-sora font-black text-2xl leading-none text-blue-600">
              {analytics?.usuarios_online?.total_online ?? '—'}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">Usuários logados</p>
          </div>
        </Link>
        <Link to="/admin/analytics"
          className="card p-4 flex items-center gap-3 hover:shadow-md transition-all">
          <div className="w-11 h-11 rounded-xl bg-purple-50 flex items-center justify-center flex-shrink-0">
            <Eye size={20} className="text-purple-500"/>
          </div>
          <div>
            <p className="font-sora font-black text-2xl leading-none text-purple-600">
              {analytics?.acessos?.hoje ?? '—'}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">Visitas hoje</p>
          </div>
        </Link>
        <Link to="/admin/analytics"
          className="card p-4 flex items-center gap-3 hover:shadow-md transition-all">
          <div className="w-11 h-11 rounded-xl bg-gray-50 flex items-center justify-center flex-shrink-0">
            <Globe size={20} className="text-gray-500"/>
          </div>
          <div>
            <p className="font-sora font-black text-2xl leading-none text-gray-600">
              {analytics?.acessos?.total_geral ?? '—'}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">Total de visitas</p>
          </div>
        </Link>
      </div>

      {/* ── Bloco 1: Usuários e Vagas ── */}
      <div className="mb-2">
        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">👥 Usuários & Vagas</p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <StatCard valor={n(s.total_candidatos)} label="Candidatos cadastrados"
          sub={n(s.candidatos_semana) > 0 ? `+${n(s.candidatos_semana)} esta semana` : null}
          icon={Users} cor="text-acre-green" bg="bg-acre-green/10" to="/admin/candidatos" />

        <StatCard valor={n(s.total_empresas)} label="Empresas cadastradas"
          sub={n(s.empresas_semana) > 0 ? `+${n(s.empresas_semana)} esta semana` : null}
          icon={Building2} cor="text-blue-600" bg="bg-blue-50" to="/admin/empresas" />

        <StatCard valor={n(s.total_vagas_abertas)} label="Vagas em aberto"
          sub={n(s.novas_vagas_hoje) > 0 ? `+${n(s.novas_vagas_hoje)} hoje` : 'Nenhuma nova hoje'}
          icon={Briefcase} cor="text-emerald-600" bg="bg-emerald-50" />

        <StatCard valor={n(s.total_vagas_fechadas)} label="Vagas encerradas"
          sub={`${n(s.total_vagas)} total de vagas`}
          icon={XCircle} cor="text-gray-500" bg="bg-gray-100" />
      </div>

      {/* ── Bloco 2: Processo Seletivo ── */}
      <div className="mb-2">
        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">🎯 Processo Seletivo</p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <StatCard valor={n(s.candidatos_em_selecao)} label="Em seleção"
          icon={UserCheck} cor="text-blue-600" bg="bg-blue-50" />
        <StatCard valor={n(s.candidatos_entrevista)} label="Em entrevista"
          icon={Activity} cor="text-purple-600" bg="bg-purple-50" />
        <StatCard valor={n(s.candidatos_aprovados)} label="Candidatos aprovados"
          icon={CheckCircle} cor="text-green-600" bg="bg-green-50" />
        <StatCard valor={n(s.total_curtidas)} label="Curtidas / Matches"
          sub={`${n(s.total_matches)} matches calculados`}
          icon={Heart} cor="text-pink-500" bg="bg-pink-50" />
      </div>

      {/* ── Bloco 3: Financeiro & Acesso ── */}
      <div className="mb-2">
        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">💰 Financeiro & Acessos</p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <StatCard valor={`R$ ${Number(s.receita_total || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
          label="Receita total" icon={DollarSign} cor="text-green-600" bg="bg-green-50" to="/admin/financeiro" />

        <StatCard valor={n(s.empresas_ativas)} label="Empresas com acesso ativo"
          icon={TrendingUp} cor="text-acre-green" bg="bg-acre-green/10" />

        <StatCard valor={n(s.pagamentos_pendentes)} label="Pagamentos pendentes"
          icon={Clock} cor="text-yellow-600" bg="bg-yellow-50"
          destaque={n(s.pagamentos_pendentes) > 0} to="/admin/pagamentos" />

        <StatCard valor={n(s.empresas_expiradas)} label="Acessos expirados"
          icon={AlertTriangle} cor="text-red-500" bg="bg-red-50"
          destaque={n(s.empresas_expiradas) > 0} />
      </div>

      {/* ── Bloco 4: Detalhe de empresas + gráfico simples ── */}
      <div className="grid md:grid-cols-3 gap-5 mb-5">

        {/* Status de acesso empresas */}
        <div className="card p-5">
          <h2 className="section-title mb-4 flex items-center gap-2">
            <Building2 size={14} className="text-blue-600"/> Empresas por Status
          </h2>
          {[
            { label: 'Ativas',           val: n(s.empresas_ativas),     cor: 'bg-green-500',  txt: 'text-green-600'  },
            { label: 'Aguard. aprovação',val: n(s.empresas_aguardando), cor: 'bg-yellow-400', txt: 'text-yellow-600' },
            { label: 'Pendentes',        val: n(s.empresas_pendentes),  cor: 'bg-orange-400', txt: 'text-orange-600' },
            { label: 'Expiradas',        val: n(s.empresas_expiradas),  cor: 'bg-red-400',    txt: 'text-red-500'    },
          ].map((item) => (
            <div key={item.label} className="mb-3 last:mb-0">
              <div className="flex justify-between items-center mb-1">
                <span className="text-xs text-gray-500">{item.label}</span>
                <span className={`text-xs font-bold ${item.txt}`}>{item.val}</span>
              </div>
              <MiniBar valor={item.val} total={n(s.total_empresas)} cor={item.cor} />
            </div>
          ))}
        </div>

        {/* Candidatos destaques */}
        <div className="card p-5">
          <h2 className="section-title mb-4 flex items-center gap-2">
            <Users size={14} className="text-acre-green"/> Candidatos Destaques
          </h2>
          <div className="space-y-3">
            {[
              { label: 'Total cadastrados',  val: n(s.total_candidatos),   cor: 'text-acre-green',  icon: '👥' },
              { label: 'Candidatos Ouro ⭐', val: n(s.candidatos_ouro),    cor: 'text-yellow-600',  icon: '🏆' },
              { label: 'Aprovados',          val: n(s.candidatos_aprovados),cor: 'text-green-600',  icon: '✅' },
              { label: 'Novos hoje',         val: n(s.novos_candidatos_hoje),cor: 'text-blue-600',  icon: '🆕' },
              { label: 'Novos na semana',    val: n(s.candidatos_semana),  cor: 'text-purple-600',  icon: '📅' },
            ].map(item => (
              <div key={item.label} className="flex items-center justify-between py-1.5 border-b border-gray-50 last:border-0">
                <span className="text-xs text-gray-500 flex items-center gap-1.5">{item.icon} {item.label}</span>
                <span className={`font-sora font-bold text-base ${item.cor}`}>{item.val}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Vagas detalhadas */}
        <div className="card p-5">
          <h2 className="section-title mb-4 flex items-center gap-2">
            <Briefcase size={14} className="text-emerald-600"/> Vagas Detalhadas
          </h2>
          <div className="space-y-3">
            {[
              { label: 'Em aberto',         val: n(s.total_vagas_abertas),  cor: 'text-emerald-600', icon: '🟢' },
              { label: 'Encerradas',        val: n(s.total_vagas_fechadas), cor: 'text-gray-500',    icon: '🔴' },
              { label: 'Total publicadas',  val: n(s.total_vagas),          cor: 'text-gray-700',    icon: '📋' },
              { label: 'Novas hoje',        val: n(s.novas_vagas_hoje),     cor: 'text-blue-600',    icon: '🆕' },
              { label: 'Matches gerados',   val: n(s.total_matches),        cor: 'text-pink-500',    icon: '💚' },
            ].map(item => (
              <div key={item.label} className="flex items-center justify-between py-1.5 border-b border-gray-50 last:border-0">
                <span className="text-xs text-gray-500 flex items-center gap-1.5">{item.icon} {item.label}</span>
                <span className={`font-sora font-bold text-base ${item.cor}`}>{item.val}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Bloco 5: Pagamentos + Expirando + Ações ── */}
      <div className="grid md:grid-cols-2 gap-5">

        {/* Pagamentos pendentes */}
        <div className="card p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="section-title flex items-center gap-2">
              <Clock size={14} className="text-yellow-500"/> Pagamentos Pendentes
            </h2>
            <Link to="/admin/pagamentos" className="text-xs text-acre-green font-semibold hover:underline">Ver todos →</Link>
          </div>
          {!pr.filter(p => p.status === 'aguardando_analise').length ? (
            <div className="text-center py-6">
              <CheckCircle size={28} className="text-green-400 mx-auto mb-2"/>
              <p className="text-gray-400 text-sm">Nenhum pagamento pendente</p>
            </div>
          ) : (
            <div className="space-y-2">
              {pr.filter(p => p.status === 'aguardando_analise').slice(0, 4).map(p => (
                <div key={p.id} className="flex items-center justify-between p-3 bg-yellow-50 border border-yellow-100 rounded-xl">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-acre-dark truncate">{p.razao_social}</p>
                    <p className="text-xs text-gray-400">
                      {new Date(p.data_solicitacao).toLocaleDateString('pt-BR')} · R$ {Number(p.valor).toFixed(2)}
                    </p>
                  </div>
                  <Link to="/admin/pagamentos" className="btn-primary text-xs px-3 py-1.5 ml-2 flex-shrink-0">Analisar</Link>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Expirando em breve + ações rápidas */}
        <div className="space-y-4">
          {/* Expirando */}
          <div className="card p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="section-title flex items-center gap-2">
                <AlertTriangle size={14} className="text-orange-500"/> Expirando em 7 dias
              </h2>
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${ex.length > 0 ? 'bg-orange-100 text-orange-600' : 'bg-gray-100 text-gray-400'}`}>
                {ex.length}
              </span>
            </div>
            {!ex.length ? (
              <p className="text-gray-400 text-sm text-center py-2">Nenhum expirando</p>
            ) : (
              <div className="space-y-1.5">
                {ex.slice(0, 3).map(e => (
                  <div key={e.id} className="flex items-center justify-between p-2.5 bg-orange-50 border border-orange-100 rounded-xl">
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-semibold text-acre-dark truncate">{e.razao_social}</p>
                      <p className="text-xs text-orange-600">Expira: {new Date(e.data_expiracao).toLocaleDateString('pt-BR')}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Ações rápidas */}
          <div className="card p-4">
            <h2 className="section-title mb-3">⚡ Ações Rápidas</h2>
            <div className="grid grid-cols-2 gap-2">
              {[
                { to: '/admin/pagamentos',   emoji: '💰', label: 'Pagamentos'  },
                { to: '/admin/empresas',     emoji: '🏢', label: 'Empresas'    },
                { to: '/admin/candidatos',   emoji: '👥', label: 'Candidatos'  },
                { to: '/admin/usuarios',     emoji: '🔑', label: 'Usuários'    },
                { to: '/admin/logs',         emoji: '📋', label: 'Logs'        },
                { to: '/admin/configuracoes',emoji: '⚙️', label: 'Config'      },
                { to: '/admin/debug',        emoji: '🔧', label: 'Debug'       },
                { to: '/admin/financeiro',   emoji: '📊', label: 'Financeiro'  },
              ].map(a => (
                <Link key={a.to} to={a.to}
                  className="flex items-center gap-2 p-2.5 rounded-xl hover:bg-gray-50 border border-gray-100 hover:border-acre-green/30 transition-all text-sm text-gray-600 hover:text-acre-dark">
                  <span>{a.emoji}</span> {a.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

    </Layout>
  );
}
